import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, Space, Typography, Collapse, Row, Col, Divider, Table, Tag, Button, Tooltip, Form, message, Tabs, DatePicker, InputNumber, Modal } from 'antd';
import { useMutation } from 'react-apollo';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { calculateImpressions, validateLineItemForDelivery } from '../../CampaignRequestor/CampaignRequestorUtil';
import { formatCurrency2SigFig, kmToFt } from '../../../../core/utils/campaigns';
import { audienceTypeMap, stateList } from '../../../../core/utils/constants/constants';
import { CampaignOrderQueueReviewAdGroup } from './CampaignOrderQueueReviewAdGroup';
import { CampaignOrderQueueReviewVideoAssets } from './CampaignOrderQueueReviewVideoAssets';
import { DownloadOutlined, CalendarOutlined, GiftOutlined, LockOutlined, RocketOutlined, CloudUploadOutlined, SyncOutlined, EditOutlined, PlusCircleOutlined, PlusOutlined, DeleteOutlined, CopyOutlined, SaveOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { capitalize } from 'lodash';
import { CampaignOrderQueueReviewCampaignLaunchDetails } from './CampaignOrderQueueReviewCampaignLaunchDetails';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { usePermify } from '@permify/react-role';
import { isPercentageModel } from '../../../../core/utils/pricing/cpmHelper';
import UPDATE_LINE_ITEM_MARKED_FOR_DELIVERY from '../../../../core/GraphQl/Mutations/UPDATE_LINE_ITEM_MARKED_FOR_DELIVERY';
import UPDATE_LINE_ITEM_DSP_SETTINGS from '../../../../core/GraphQl/Mutations/UPDATE_LINE_ITEM_DSP_SETTINGS';
import CampaignRequestorInventoryDialog from '../../CampaignRequestor/CampaignRequestorInventoryDialog';
import CampaignRequestorNexxenSegmentsDialog from '../../CampaignRequestor/CampaignRequestorNexxenSegmentsDialog';

const { Panel } = Collapse;
const { Title, Text } = Typography;

export const CampaignOrderQueueReviewCampaign = ({
  lineItemIndex,
  campaignIndex,
  restField,
  form,
  dspType,
  setDspType,
  fieldName,
  onMediaStatusUpdate,
  isLaunching = false,
  onSave,
  isSaving = false,
  campaignFlightSchedules = [],
  campaignOrderData,
}) => {
  const { timezone } = useTimezone();
  const { currentCampaignOrder } = useCampaignRequestorContext();

  // Mutation for updating markedForDelivery
  const [updateLineItemMarkedForDelivery, { loading: updatingMarkedForDelivery }] = useMutation(UPDATE_LINE_ITEM_MARKED_FOR_DELIVERY);

  // Mutation for updating DSP settings
  const [updateLineItemDspSettings, { loading: savingDspSettings }] = useMutation(UPDATE_LINE_ITEM_DSP_SETTINGS);
  const { isRegulated, pricingModel } = currentCampaignOrder;
  const campaignType = currentCampaignOrder.campaigns[campaignIndex]?.type;
  const isVideoType = campaignType === 'VIDEO' || campaignType === 8;
  const lineItem = currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex]
    || form.getFieldValue(['campaigns', campaignIndex, 'lineItems', lineItemIndex])
    || {};

  // Use campaignOrderData for adGroup if available (for immediate UI updates after bulk approval)
  // Fall back to context data otherwise
  const lineItemForUI = campaignOrderData?.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex];
  const adGroupForUI = lineItemForUI?.adGroup || lineItem.adGroup;

  const {
    name,
    startDate,
    endDate,
    budgetTotal,
    cpm,
    adGroup,
    firstPartyDataAudiences,
    audienceType,
    targetingType,
    targetingRegions,
    targetingLocations,
    targetingCountries,
    valueAdd,
    isOrigin,
    markedForDelivery: contextMarkedForDelivery,
    dspLineItemId,
    dspDealIds: contextDspDealIds,
    segments: contextSegments,
    inventorySources: contextInventorySources,
    inventoryLists: contextInventoryLists,
  } = lineItem;

  // Watch markedForDelivery from form for reactive updates
  const formMarkedForDelivery = Form.useWatch(
    ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'markedForDelivery'],
    form
  );
  // Use form value if available, otherwise fall back to context value
  const markedForDelivery = formMarkedForDelivery ?? contextMarkedForDelivery;

  // Watch inventory fields from form for reactive updates
  const formInventorySources = Form.useWatch(
    ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'inventorySources'],
    form
  );
  const formInventoryLists = Form.useWatch(
    ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'inventoryLists'],
    form
  );
  const formDspDealIds = Form.useWatch(
    ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'dspDealIds'],
    form
  );
  const formSegments = Form.useWatch(
    ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'segments'],
    form
  );

  // Use form values if available, otherwise fall back to context values
  const inventorySources = formInventorySources ?? contextInventorySources;
  const inventoryLists = formInventoryLists ?? contextInventoryLists;
  const dspDealIds = formDspDealIds ?? contextDspDealIds;
  const segments = formSegments ?? contextSegments;

  // Check if this is a programmatic campaign and line item is already launched
  const campaign = currentCampaignOrder?.campaigns?.[campaignIndex];
  const isProgrammaticCampaign = campaign?.isLaunchProgrammatically === true;
  const isLineItemLaunched = !!dspLineItemId;

  // Check if line item is ready for launch (programmatic campaign, not yet launched, marked for delivery)
  const isReadyForLaunch = isProgrammaticCampaign && !isLineItemLaunched && markedForDelivery;

  // Detect if line item has pending changes (status-based detection)
  const isLineItemModified = useMemo(() => {
    // Only show MODIFIED badge for programmatic campaigns with launched line items
    if (!isProgrammaticCampaign || !isLineItemLaunched) return false;

    // Check if status is PENDING (indicates changes waiting to be applied)
    const status = lineItem?.status;
    return status === 'PLAY_PENDING' || status === 'PAUSED_PENDING';
  }, [isProgrammaticCampaign, isLineItemLaunched, lineItem.status]);

  // Permission checking for Flight Schedules visibility and Mark for Delivery
  const { isAuthorized } = usePermify();
  const [showFlightSchedules, setShowFlightSchedules] = useState(false);
  const [hasTraffickerOrProcessorPermission, setHasTraffickerOrProcessorPermission] = useState(false);

  // Determine if DSP Settings tab should be shown (not shown for PENDING statuses)
  const showDspSettingsTab = !['PENDING', 'LIVE_PENDING', 'PAUSED_PENDING'].includes(currentCampaignOrder?.status);

  // Active tab state - default to DSP Settings when available
  const [activeTabKey, setActiveTabKey] = useState('details');
  const hasSetInitialTab = useRef(false);

  // State for inventory configuration dialog
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);

  // State for Nexxen segments dialog
  const [showNexxenSegmentsDialog, setShowNexxenSegmentsDialog] = useState(false);

  // Set initial active tab to DSP Settings when it becomes available
  useEffect(() => {
    if (showDspSettingsTab && !hasSetInitialTab.current) {
      setActiveTabKey('dsp');
      hasSetInitialTab.current = true;
    }
  }, [showDspSettingsTab]);

  // Check if user can see Flight Schedules and Mark for Delivery button
  useEffect(() => {
    const checkPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);

      // Set permission state for Mark for Delivery button
      setHasTraffickerOrProcessorPermission(hasAdOpsPermission);

      // Flight Schedules: Hide if DRAFT status, otherwise show only to Ad Ops
      if (currentCampaignOrder?.status === 'DRAFT') {
        setShowFlightSchedules(false);
      } else {
        setShowFlightSchedules(hasAdOpsPermission);
      }
    };

    checkPermissions();
  }, [currentCampaignOrder.status, isAuthorized]);

  // Check if pricing model is Percentage (hide CPM validation)
  const hideCpmFields = isPercentageModel(currentCampaignOrder?.advertiser);

  // Flight Schedule state and management
  const [flightSchedule, setFlightSchedule] = useState(lineItem.flightSchedules || []);
  const [hasInitializedFlightSchedule, setHasInitializedFlightSchedule] = useState(false);
  const campaignsRef = useRef(null);

  // Initialize flight schedule from line item data
  useEffect(() => {
    const existingFlights = lineItem.flightSchedules || [];
    if (!hasInitializedFlightSchedule && existingFlights.length > 0) {
      setFlightSchedule(existingFlights);
      setHasInitializedFlightSchedule(true);
    }
  }, [lineItem.flightSchedules, hasInitializedFlightSchedule]);

  // Sync flight schedule changes to form
  useEffect(() => {
    if (!form) return;

    const campaigns = form.getFieldValue('campaigns') || [];
    campaignsRef.current = campaigns;

    // Check if flight schedule has changed
    const currentFormFlightSchedule = campaigns[campaignIndex]?.lineItems?.[lineItemIndex]?.flightSchedule || [];
    const hasChanged = JSON.stringify(currentFormFlightSchedule) !== JSON.stringify(flightSchedule);

    if (hasChanged && flightSchedule.length >= 0) {
      const updatedCampaigns = [...campaigns];
      if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
        updatedCampaigns[campaignIndex].lineItems[lineItemIndex].flightSchedule = flightSchedule;
        form.setFieldsValue({ campaigns: updatedCampaigns });
      }
    }
  }, [flightSchedule, form, campaignIndex, lineItemIndex]);

  // Get campaign flight schedules for "Copy from Campaign" feature
  // Use prop (from parent's local state) first, then fall back to context (database)
  const getCampaignFlightSchedules = () => {
    if (campaignFlightSchedules && campaignFlightSchedules.length > 0) {
      return campaignFlightSchedules;
    }
    const parentCampaign = currentCampaignOrder.campaigns[campaignIndex];
    return parentCampaign?.flightSchedule || parentCampaign?.flightSchedules || [];
  };

  // Handle copy flight schedules from campaign
  const handleCopyFromCampaign = () => {
    const campaignFlights = getCampaignFlightSchedules();
    const copiedFlights = campaignFlights.map((flight) => ({
      startDate: flight.startDate,
      endDate: flight.endDate,
      budget: flight.budget || null,
      ...(hideCpmFields ? {} : { cpm: flight.cpm || lineItem.cpm || 7 }),
    }));
    setFlightSchedule(copiedFlights);
  };

  // Handle copy from campaign button click with confirmation
  const handleCopyFromCampaignClick = () => {
    const campaignFlights = getCampaignFlightSchedules();

    if (flightSchedule.length > 0) {
      Modal.confirm({
        title: 'Replace existing flight schedules?',
        content: `This will replace ${flightSchedule.length} existing flight(s) with ${campaignFlights.length} flight(s) from the campaign. This action cannot be undone.`,
        okText: 'Replace',
        cancelText: 'Cancel',
        onOk: handleCopyFromCampaign,
      });
    } else {
      handleCopyFromCampaign();
    }
  };

  // Handle Save DSP Settings
  const handleSaveDspSettings = async () => {
    try {
      // Get the current form values for this line item
      const formValues = form.getFieldsValue();
      const lineItemFormValues = formValues.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex];

      if (!lineItemFormValues) {
        message.error('Could not get form values');
        return;
      }

      // Get original flight schedules from the line item
      const originalFlightSchedules = lineItem.flightSchedules || [];

      // Separate current flights into new (no ID) and existing (has ID)
      const newFlights = flightSchedule.filter(f => !f.id);
      const existingFlights = flightSchedule.filter(f => f.id);

      // Build flight schedules payload
      const flightSchedulesPayload = {};

      // Create new flights
      if (newFlights.length > 0) {
        flightSchedulesPayload.create = newFlights.map(flight => ({
          startDate: flight.startDate,
          endDate: flight.endDate,
          budget: Number(flight.budget) || 0,
          ...(flight.cpm !== undefined ? { cpm: Number(flight.cpm) || 7 } : {}),
        }));
      }

      // Delete removed flights (flights in original but not in current)
      const currentFlightIds = new Set(existingFlights.map(f => f.id));
      const flightsToDelete = originalFlightSchedules.filter(orig => !currentFlightIds.has(orig.id));
      if (flightsToDelete.length > 0) {
        flightSchedulesPayload.delete = flightsToDelete.map(f => ({ id: f.id }));
      }

      // Update existing flights
      const flightsToUpdate = existingFlights.map(flight => ({
        where: { id: flight.id },
        data: {
          startDate: flight.startDate,
          endDate: flight.endDate,
          budget: Number(flight.budget) || 0,
          ...(flight.cpm !== undefined ? { cpm: Number(flight.cpm) || 7 } : {}),
        },
      }));
      if (flightsToUpdate.length > 0) {
        flightSchedulesPayload.update = flightsToUpdate;
      }

      // Build the line item update data
      const lineItemUpdateData = {
        supplySidePlatform: lineItemFormValues.supplySidePlatform || null,
        maxBid: lineItemFormValues.maxBid ? parseFloat(lineItemFormValues.maxBid) : null,
        frequencyCap: lineItemFormValues.frequencyCap ? parseInt(lineItemFormValues.frequencyCap, 10) : null,
      };

      // Add flight schedules if there are any changes
      if (Object.keys(flightSchedulesPayload).length > 0) {
        lineItemUpdateData.flightSchedules = flightSchedulesPayload;
      }

      // If DSP Line Item ID is present, include it
      if (lineItemFormValues.dspLineItemId) {
        lineItemUpdateData.dspLineItemId = lineItemFormValues.dspLineItemId;
      }

      // If beacons are present, include them
      if (lineItemFormValues.beacons && lineItemFormValues.beacons.length > 0) {
        const originalBeacons = lineItem.beacons || [];
        const newBeacons = lineItemFormValues.beacons.filter(b => !b.id);
        const existingBeacons = lineItemFormValues.beacons.filter(b => b.id);

        const beaconsPayload = {};

        if (newBeacons.length > 0) {
          beaconsPayload.create = newBeacons.map(beacon => ({
            beaconId: beacon.beaconId,
            name: beacon.name,
            beaconType: beacon.beaconType,
          }));
        }

        const currentBeaconIds = new Set(existingBeacons.map(b => b.id));
        const beaconsToDelete = originalBeacons.filter(orig => !currentBeaconIds.has(orig.id));
        if (beaconsToDelete.length > 0) {
          beaconsPayload.delete = beaconsToDelete.map(b => ({ id: b.id }));
        }

        if (Object.keys(beaconsPayload).length > 0) {
          lineItemUpdateData.beacons = beaconsPayload;
        }
      }

      // Build inventory sources payload
      const currentInventorySources = lineItemFormValues.inventorySources || inventorySources || [];
      const originalInventorySources = lineItem.inventorySources || [];
      const inventorySourcesPayload = {};

      // Find new sources (no id means newly added)
      const newSources = currentInventorySources.filter(s => !s.id);
      if (newSources.length > 0) {
        inventorySourcesPayload.create = newSources.map(source => ({
          sourceId: source.sourceId,
          name: source.name,
        }));
      }

      // Find deleted sources (in original but not in current)
      const currentSourceIds = new Set(currentInventorySources.filter(s => s.id).map(s => s.id));
      const sourcesToDelete = originalInventorySources.filter(orig => !currentSourceIds.has(orig.id));
      if (sourcesToDelete.length > 0) {
        inventorySourcesPayload.delete = sourcesToDelete.map(s => ({ id: s.id }));
      }

      if (Object.keys(inventorySourcesPayload).length > 0) {
        lineItemUpdateData.inventorySources = inventorySourcesPayload;
      }

      // Build inventory lists payload
      const currentInventoryLists = lineItemFormValues.inventoryLists || inventoryLists || [];
      const originalInventoryLists = lineItem.inventoryLists || [];
      const inventoryListsPayload = {};

      // Find new lists (no id means newly added)
      const newLists = currentInventoryLists.filter(l => !l.id);
      if (newLists.length > 0) {
        inventoryListsPayload.create = newLists.map(list => ({
          listId: list.listId,
          name: list.name,
          action: list.action || 'include',
        }));
      }

      // Find deleted lists (in original but not in current)
      const currentListIds = new Set(currentInventoryLists.filter(l => l.id).map(l => l.id));
      const listsToDelete = originalInventoryLists.filter(orig => !currentListIds.has(orig.id));
      if (listsToDelete.length > 0) {
        inventoryListsPayload.delete = listsToDelete.map(l => ({ id: l.id }));
      }

      // Find updated lists (action changed)
      const listsToUpdate = currentInventoryLists
        .filter(l => l.id)
        .filter(l => {
          const original = originalInventoryLists.find(orig => orig.id === l.id);
          return original && original.action !== l.action;
        })
        .map(l => ({
          where: { id: l.id },
          data: { action: l.action },
        }));
      if (listsToUpdate.length > 0) {
        inventoryListsPayload.update = listsToUpdate;
      }

      if (Object.keys(inventoryListsPayload).length > 0) {
        lineItemUpdateData.inventoryLists = inventoryListsPayload;
      }

      // Build DSP deal IDs payload
      const currentDspDealIds = lineItemFormValues.dspDealIds || dspDealIds || [];
      const originalDspDealIds = lineItem.dspDealIds || [];
      const dspDealIdsPayload = {};

      // Find new deals (no id means newly added)
      const newDeals = currentDspDealIds.filter(d => !d.id);
      if (newDeals.length > 0) {
        dspDealIdsPayload.create = newDeals.map(deal => ({
          dealId: deal.dealId,
          name: deal.name,
          cpmBid: deal.cpmBid || null,
        }));
      }

      // Find deleted deals (in original but not in current)
      const currentDealIds = new Set(currentDspDealIds.filter(d => d.id).map(d => d.id));
      const dealsToDelete = originalDspDealIds.filter(orig => !currentDealIds.has(orig.id));
      if (dealsToDelete.length > 0) {
        dspDealIdsPayload.delete = dealsToDelete.map(d => ({ id: d.id }));
      }

      if (Object.keys(dspDealIdsPayload).length > 0) {
        lineItemUpdateData.dspDealIds = dspDealIdsPayload;
      }

      // Build segments payload
      const currentSegments = lineItemFormValues.segments || segments || [];
      const originalSegments = lineItem.segments || [];
      const segmentsPayload = {};

      // Find new segments (no id means newly added)
      const newSegments = currentSegments.filter(s => !s.id);
      if (newSegments.length > 0) {
        segmentsPayload.create = newSegments.map(segment => ({
          segmentId: segment.segmentId,
          name: segment.name,
          segmentType: segment.segmentType || 'segmentSet',
          cost: segment.cost || 0,
        }));
      }

      // Find deleted segments (in original but not in current)
      const currentSegmentIds = new Set(currentSegments.filter(s => s.id).map(s => s.id));
      const segmentsToDelete = originalSegments.filter(orig => !currentSegmentIds.has(orig.id));
      if (segmentsToDelete.length > 0) {
        segmentsPayload.delete = segmentsToDelete.map(s => ({ id: s.id }));
      }

      if (Object.keys(segmentsPayload).length > 0) {
        lineItemUpdateData.segments = segmentsPayload;
      }

      // Call the update mutation
      await updateLineItemDspSettings({
        variables: {
          lineItemId: lineItem.id,
          data: lineItemUpdateData,
        },
      });

      message.success('DSP Settings saved successfully');
    } catch (error) {
      console.error('Error saving DSP settings:', error);
      message.error(`Failed to save DSP Settings: ${error.message}`);
    }
  };

  // Check if status is not DRAFT (for showing Copy from Campaign button)
  const isNotDraftStatus = currentCampaignOrder?.status !== 'DRAFT';

  // Check if status allows Mark for Delivery (all statuses except DRAFT and ARCHIVED)
  const canMarkForDelivery = !['DRAFT', 'ARCHIVED'].includes(currentCampaignOrder?.status);

  // Validate line item for delivery readiness
  const validateDeliveryReadiness = useMemo(() => {
    return validateLineItemForDelivery(lineItem, hideCpmFields);
  }, [lineItem, hideCpmFields]);

  // Generate dynamic tooltip message for the Mark for Delivery button
  const deliveryButtonTooltip = useMemo(() => {
    if (markedForDelivery) {
      return 'Marked for Delivery';
    }
    if (!validateDeliveryReadiness.isValid) {
      const fieldsList = validateDeliveryReadiness.missingFields.join(', ');
      return `Cannot mark for delivery. Missing required fields: ${fieldsList}`;
    }
    return 'Mark for Delivery';
  }, [markedForDelivery, validateDeliveryReadiness]);

  // Handle Mark for Delivery button click
  const handleMarkForDelivery = async () => {
    const newMarkedForDelivery = !markedForDelivery;

    // Update form state for immediate UI feedback
    const campaigns = form.getFieldValue('campaigns');
    const updatedCampaigns = campaigns.map((c, cIdx) => {
      if (cIdx === campaignIndex) {
        return {
          ...c,
          lineItems: c.lineItems.map((li, liIdx) => {
            if (liIdx === lineItemIndex) {
              return { ...li, markedForDelivery: newMarkedForDelivery };
            }
            return li;
          }),
        };
      }
      return c;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });

    // If line item has been saved (has an id), persist to database
    if (lineItem.id) {
      try {
        await updateLineItemMarkedForDelivery({
          variables: {
            lineItemId: lineItem.id,
            data: { markedForDelivery: newMarkedForDelivery },
          },
        });
        message.success(
          newMarkedForDelivery
            ? 'Line item marked for delivery'
            : 'Line item unmarked for delivery'
        );
      } catch (error) {
        message.error(`Failed to update delivery status: ${error.message}`);
        // Revert form state on error
        form.setFieldsValue({ campaigns });
      }
    }
  };

  // Handle saving inventory configuration from the dialog
  const handleSaveInventoryConfig = ({ sources, lists, deals }) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex] = {
        ...updatedCampaigns[campaignIndex].lineItems[lineItemIndex],
        inventorySources: sources,
        inventoryLists: lists,
        dspDealIds: deals,
      };
      form.setFieldsValue({ campaigns: updatedCampaigns });
      message.success('Inventory configuration updated');
    }
  };

  // Handle saving Nexxen segment selections from the dialog
  const handleSaveSegments = (selectedSegments) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex] = {
        ...updatedCampaigns[campaignIndex].lineItems[lineItemIndex],
        segments: selectedSegments,
      };
      form.setFieldsValue({ campaigns: updatedCampaigns });
      message.success('Segment configuration updated');
    }
  };

  // Determine if DSP settings fields should be disabled (for launched line items)
  const isFieldDisabled = isLaunching || (isProgrammaticCampaign && isLineItemLaunched && ['LIVE', 'PAUSED'].includes(lineItem.status));

  const getStateCode = (state, stateList) => {
    const stateEntry = stateList.find((val) => state === val.State);
    return stateEntry ? stateEntry.Code : '';
  };

  const processGeoTargets = (geoTarget, stateList) => {
    return geoTarget.circles.map((circle) => {
      const location = circle.location || geoTarget.location || {};
      const state = location.state ? capitalize(location.state) : '';
      const stateCode = getStateCode(state, stateList);

      return {
        Name: location.name || '',
        Type: 'CIRCLE',
        Address: location.formattedAddress || '',
        Street: location.street || '',
        City: location.city || '',
        County: location.county || '',
        State: stateCode || location.state || '',
        Country: location.country || '',
        Zip: location.zip || '',
        Latitude: circle.coordinate.lat,
        Longitude: circle.coordinate.lng,
        'Radius (Miles)': Math.round(circle.radius * 1000),
      };
    });
  };

  const flattenLocations = (targetingLocations, stateList) => {
    return targetingLocations.flatMap((targetingLocation) =>
      targetingLocation.geoTargets.flatMap((geoTarget) => processGeoTargets(geoTarget, stateList))
    );
  };

  const flattenRegions = (targetingRegions) => {
    return targetingRegions.flatMap((regionGroup) =>
      regionGroup.regions.map((region) => ({
        'Region Group Name': regionGroup.name,
        'Region': region,
      }))
    );
  };

  const csvData = flattenLocations(targetingLocations, stateList);
  const regionCsvData = flattenRegions(targetingRegions || []);

  return (
    <div id={`campaign-${campaignIndex}-lineitem-${lineItemIndex}`}>
      <Collapse bordered={false} defaultActiveKey={[0]}>
        <Panel open={true} header={
          <Space size="small" align="center">
            <Title level={5} style={{ margin: 0 }}>{name || `Line Item ${lineItemIndex + 1}`}</Title>
            {!lineItem.id && (
              <Tooltip title="New line item (not yet saved)">
                <Tag icon={<PlusCircleOutlined />} color="cyan">
                  New
                </Tag>
              </Tooltip>
            )}
            {valueAdd && (
              <Tooltip title="Value Add">
                <Tag icon={<GiftOutlined />} color="purple">
                  Value Add
                </Tag>
              </Tooltip>
            )}
            {isOrigin && (
              <Tooltip title="Origin (Client Created)">
                <Tag icon={<LockOutlined />} color="blue">
                  Origin
                </Tag>
              </Tooltip>
            )}
            {markedForDelivery && (
              <Tooltip title="Marked for Delivery">
                <Tag icon={<RocketOutlined />} color="green">
                  Delivery
                </Tag>
              </Tooltip>
            )}
            {/* Mark for Delivery button - visible when user has permission and status is not DRAFT */}
            {hasTraffickerOrProcessorPermission && canMarkForDelivery && (
              <Tooltip title={deliveryButtonTooltip}>
                <Button
                  shape="circle"
                  size="small"
                  icon={<RocketOutlined />}
                  loading={updatingMarkedForDelivery}
                  disabled={updatingMarkedForDelivery || (!markedForDelivery && !validateDeliveryReadiness.isValid)}
                  style={{
                    backgroundColor: markedForDelivery ? '#52c41a' : '#fff',
                    borderColor: markedForDelivery ? '#52c41a' : (validateDeliveryReadiness.isValid ? '#52c41a' : '#d9d9d9'),
                    color: markedForDelivery
                      ? '#fff'
                      : (validateDeliveryReadiness.isValid ? '#52c41a' : 'rgba(0, 0, 0, 0.25)'),
                    cursor: (updatingMarkedForDelivery || (!markedForDelivery && !validateDeliveryReadiness.isValid))
                      ? 'not-allowed'
                      : 'pointer',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!updatingMarkedForDelivery && (markedForDelivery || validateDeliveryReadiness.isValid)) {
                      handleMarkForDelivery();
                    }
                  }}
                />
              </Tooltip>
            )}
            {isReadyForLaunch && (
              <Tooltip title="New line item ready for programmatic launch">
                <Tag icon={<SyncOutlined />} color="orange">
                  Ready for Launch
                </Tag>
              </Tooltip>
            )}
            {isProgrammaticCampaign && isLineItemLaunched && (
              <Tooltip title="Line item has been launched to the DSP">
                <Tag icon={<CloudUploadOutlined />} color="purple">
                  Launched to DSP
                </Tag>
              </Tooltip>
            )}
            {isLineItemModified && isProgrammaticCampaign && isLineItemLaunched && (
              <Tooltip title="Line item has been modified and is ready to be updated when re-launched">
                <Tag icon={<EditOutlined />} color="orange">
                  Ready for Update
                </Tag>
              </Tooltip>
            )}
          </Space>
        }>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card bodyStyle={{ padding: 0 }}>
                <Tabs
                  type="card"
                  style={{ padding: '16px 24px 0' }}
                  activeKey={activeTabKey}
                  onChange={setActiveTabKey}
                  items={[
                    // DSP Settings tab - shown when Advanced Mode and not in PENDING status (placed first as default)
                    ...(showDspSettingsTab ? [{
                      key: 'dsp',
                      label: 'DSP Settings',
                      children: (
                        <Space direction="vertical" size="middle" style={{ width: '100%', paddingTop: 16, paddingBottom: 24 }}>
                          {/* Launch Details Form */}
                          <CampaignOrderQueueReviewCampaignLaunchDetails
                            form={form}
                            fieldName={fieldName}
                            campaignIndex={campaignIndex}
                            lineItemIndex={lineItemIndex}
                            dspType={dspType}
                            setDspType={setDspType}
                            isLaunching={isLaunching}
                          />

                          {/* Flight Schedules Section - Editable */}
                          {showFlightSchedules && (
                            <Card title={<><CalendarOutlined style={{ marginRight: 8 }} />FLIGHT SCHEDULE</>} size="small">
                              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                  <div>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                      Configure multiple flight date ranges for phased execution
                                    </Text>
                                  </div>
                                  <Space>
                                    {/* Copy from Campaign button */}
                                    {isNotDraftStatus && (() => {
                                      const hasCampaignFlights = getCampaignFlightSchedules().length > 0;
                                      return (
                                        <Tooltip
                                          title={!hasCampaignFlights ? 'Campaign has no flight schedules to copy' : undefined}
                                        >
                                          <Button
                                            icon={<CopyOutlined />}
                                            disabled={!hasCampaignFlights || isLaunching}
                                            onClick={handleCopyFromCampaignClick}
                                          >
                                            Copy from Campaign
                                          </Button>
                                        </Tooltip>
                                      );
                                    })()}
                                    <Button
                                      type="primary"
                                      icon={<PlusOutlined />}
                                      disabled={isLaunching}
                                      onClick={() => {
                                        let newStartDate, newEndDate;

                                        if (flightSchedule.length === 0) {
                                          newStartDate = startDate
                                            ? moment(startDate)
                                            : moment.tz(timezone).startOf('day').utc();
                                          newEndDate = endDate
                                            ? moment(endDate)
                                            : moment.tz(timezone).add(2, 'days').endOf('day').utc();
                                        } else {
                                          let furthestEndDate = null;
                                          flightSchedule.forEach((flight) => {
                                            if (flight.endDate) {
                                              const flightEnd = moment(flight.endDate);
                                              if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
                                                furthestEndDate = flightEnd;
                                              }
                                            }
                                          });

                                          if (!furthestEndDate) {
                                            furthestEndDate = endDate ? moment(endDate) : moment.tz(timezone);
                                          }

                                          const nextDay = moment(furthestEndDate).tz(timezone).add(1, 'day').format('YYYY-MM-DD');
                                          newStartDate = moment.tz(nextDay, timezone).startOf('day').utc();
                                          newEndDate = moment.tz(nextDay, timezone).add(2, 'days').endOf('day').utc();
                                        }

                                        const newFlight = {
                                          startDate: newStartDate.toISOString(),
                                          endDate: newEndDate.toISOString(),
                                          budget: null,
                                          // Only add CPM for CPM pricing model
                                          ...(!hideCpmFields ? { cpm: cpm || 7 } : {}),
                                        };
                                        setFlightSchedule([...flightSchedule, newFlight]);
                                      }}
                                    >
                                      Add Flight
                                    </Button>
                                  </Space>
                                </Space>
                                <Table
                                  dataSource={flightSchedule.map((flight, idx) => {
                                    const numberOfDays = flight.startDate && flight.endDate
                                      ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                                      : null;
                                    const impressionGoal = flight.budget && flight.cpm
                                      ? Math.round((flight.budget / flight.cpm) * 1000)
                                      : null;
                                    return {
                                      key: idx,
                                      ...flight,
                                      numberOfDays,
                                      impressionGoal,
                                    };
                                  })}
                                  columns={[
                                    {
                                      title: 'Start Date',
                                      dataIndex: 'startDate',
                                      key: 'startDate',
                                      width: 200,
                                      render: (value, record, idx) => (
                                        <DatePicker
                                          value={value ? moment(value).tz(timezone) : null}
                                          format="MM/DD/YYYY"
                                          disabled={isLaunching}
                                          disabledDate={(current) => {
                                            if (!current) return false;
                                            const lineItemStart = startDate ? moment(startDate).tz(timezone).startOf('day') : null;
                                            const lineItemEnd = endDate ? moment(endDate).tz(timezone).endOf('day') : null;
                                            if (lineItemStart && current.isBefore(lineItemStart)) return true;
                                            if (lineItemEnd && current.isAfter(lineItemEnd)) return true;
                                            return false;
                                          }}
                                          onChange={(date) => {
                                            const updated = [...flightSchedule];
                                            const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc() : null;
                                            updated[idx] = { ...updated[idx], startDate: normalizedDate };
                                            setFlightSchedule(updated);
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      title: 'End Date',
                                      dataIndex: 'endDate',
                                      key: 'endDate',
                                      width: 200,
                                      render: (value, record, idx) => (
                                        <DatePicker
                                          value={value ? moment(value).tz(timezone) : null}
                                          format="MM/DD/YYYY"
                                          disabled={isLaunching}
                                          disabledDate={(current) => {
                                            if (!current) return false;
                                            const flightStart = record.startDate ? moment(record.startDate).tz(timezone) : null;
                                            const lineItemEnd = endDate ? moment(endDate).tz(timezone).endOf('day') : null;
                                            if (flightStart && current.isBefore(flightStart)) return true;
                                            if (lineItemEnd && current.isAfter(lineItemEnd)) return true;
                                            return false;
                                          }}
                                          onChange={(date) => {
                                            const updated = [...flightSchedule];
                                            const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc() : null;
                                            updated[idx] = { ...updated[idx], endDate: normalizedDate };
                                            setFlightSchedule(updated);
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      title: 'Budget',
                                      dataIndex: 'budget',
                                      key: 'budget',
                                      width: 150,
                                      render: (value, record, idx) => (
                                        <InputNumber
                                          value={value}
                                          min={0}
                                          precision={2}
                                          formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                          parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
                                          disabled={isLaunching}
                                          onChange={(val) => {
                                            const updated = [...flightSchedule];
                                            updated[idx] = {
                                              ...updated[idx],
                                              budget: val,
                                            };
                                            setFlightSchedule(updated);
                                          }}
                                        />
                                      ),
                                    },
                                    // CPM column - hidden for Percentage pricing
                                    ...(!hideCpmFields ? [{
                                      title: <span>CPM <Text type="danger">*</Text></span>,
                                      dataIndex: 'cpm',
                                      key: 'cpm',
                                      width: 120,
                                      render: (value, _, idx) => (
                                        <InputNumber
                                          style={{
                                            width: '100%',
                                            borderColor: (!value || value < 1) ? '#ff4d4f' : undefined
                                          }}
                                          value={value}
                                          min={0}
                                          precision={2}
                                          disabled={isLaunching}
                                          onChange={(val) => {
                                            const updated = [...flightSchedule];
                                            updated[idx] = {
                                              ...updated[idx],
                                              cpm: val,
                                            };
                                            setFlightSchedule(updated);
                                          }}
                                        />
                                      ),
                                    }] : []),
                                    // Impressions column - hidden for Percentage pricing
                                    ...(!hideCpmFields ? [{
                                      title: 'Impressions',
                                      dataIndex: 'impressionGoal',
                                      key: 'impressionGoal',
                                      width: 120,
                                      align: 'right',
                                      render: (value) => (
                                        <Text>{value ? value.toLocaleString() : '-'}</Text>
                                      ),
                                    }] : []),
                                    {
                                      title: 'Days',
                                      dataIndex: 'numberOfDays',
                                      key: 'numberOfDays',
                                      width: 80,
                                      align: 'center',
                                      render: (value) => (
                                        <Text>{value !== null ? value : '-'}</Text>
                                      ),
                                    },
                                    {
                                      title: '',
                                      key: 'actions',
                                      width: 50,
                                      align: 'center',
                                      render: (_, __, idx) => (
                                        <Button
                                          danger
                                          size="small"
                                          type="text"
                                          icon={<DeleteOutlined />}
                                          disabled={isLaunching}
                                          onClick={() => {
                                            const updated = flightSchedule.filter((_, i) => i !== idx);
                                            setFlightSchedule(updated);
                                          }}
                                        />
                                      ),
                                    },
                                  ]}
                                  pagination={false}
                                  size="small"
                                  locale={{
                                    emptyText: 'No flight dates configured. Click "Add Flight" to add a new flight.',
                                  }}
                                />
                              </Space>
                            </Card>
                          )}

                          {/* Inventory & Supply Section */}
                          <Card title="INVENTORY & SUPPLY" size="small">
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                              <div>
                                <Text strong>Inventory Configuration</Text>
                                <br />
                                <Text type="secondary">
                                  Configure inventory sources, site lists, and private deals for this line item
                                </Text>
                              </div>

                              {/* Inventory Sources Table */}
                              {inventorySources && inventorySources.length > 0 && (
                                <div>
                                  <Text strong style={{ fontSize: 13 }}>Inventory Sources (SSPs/Exchanges)</Text>
                                  <Table
                                    dataSource={inventorySources.map((source, idx) => ({
                                      ...source,
                                      key: source.sourceId || idx,
                                    }))}
                                    rowKey="sourceId"
                                    columns={[
                                      {
                                        title: 'Name',
                                        dataIndex: 'name',
                                        key: 'name',
                                        ellipsis: true,
                                      },
                                      {
                                        title: '',
                                        key: 'action',
                                        width: 60,
                                        render: (_, record) => (
                                          !isFieldDisabled && (
                                            <Button
                                              type="text"
                                              danger
                                              size="small"
                                              icon={<DeleteOutlined />}
                                              onClick={() => {
                                                const campaigns = form.getFieldValue('campaigns') || [];
                                                const updatedCampaigns = [...campaigns];
                                                if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
                                                  updatedCampaigns[campaignIndex].lineItems[lineItemIndex] = {
                                                    ...updatedCampaigns[campaignIndex].lineItems[lineItemIndex],
                                                    inventorySources: (inventorySources || []).filter(
                                                      (s) => s.sourceId !== record.sourceId
                                                    ),
                                                  };
                                                  form.setFieldsValue({ campaigns: updatedCampaigns });
                                                }
                                              }}
                                            />
                                          )
                                        ),
                                      },
                                    ]}
                                    pagination={false}
                                    size="small"
                                    style={{ marginTop: 8 }}
                                  />
                                </div>
                              )}

                              {/* Inventory Lists Table */}
                              {inventoryLists && inventoryLists.length > 0 && (
                                <div>
                                  <Text strong style={{ fontSize: 13 }}>Inventory Lists (Site/App Lists)</Text>
                                  <Table
                                    dataSource={inventoryLists.map((list, idx) => ({
                                      ...list,
                                      key: list.listId || idx,
                                    }))}
                                    rowKey="listId"
                                    columns={[
                                      {
                                        title: 'Name',
                                        dataIndex: 'name',
                                        key: 'name',
                                        ellipsis: true,
                                      },
                                      {
                                        title: 'Action',
                                        dataIndex: 'action',
                                        key: 'action',
                                        width: 100,
                                        render: (action) => (
                                          <Tag color={action === 'include' ? 'green' : 'red'}>
                                            {action === 'include' ? 'Include' : 'Exclude'}
                                          </Tag>
                                        ),
                                      },
                                      {
                                        title: '',
                                        key: 'remove',
                                        width: 60,
                                        render: (_, record) => (
                                          !isFieldDisabled && (
                                            <Button
                                              type="text"
                                              danger
                                              size="small"
                                              icon={<DeleteOutlined />}
                                              onClick={() => {
                                                const campaigns = form.getFieldValue('campaigns') || [];
                                                const updatedCampaigns = [...campaigns];
                                                if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
                                                  updatedCampaigns[campaignIndex].lineItems[lineItemIndex] = {
                                                    ...updatedCampaigns[campaignIndex].lineItems[lineItemIndex],
                                                    inventoryLists: (inventoryLists || []).filter(
                                                      (l) => l.listId !== record.listId
                                                    ),
                                                  };
                                                  form.setFieldsValue({ campaigns: updatedCampaigns });
                                                }
                                              }}
                                            />
                                          )
                                        ),
                                      },
                                    ]}
                                    pagination={false}
                                    size="small"
                                    style={{ marginTop: 8 }}
                                  />
                                </div>
                              )}

                              {/* Publisher Deals Table */}
                              {dspDealIds && dspDealIds.length > 0 && (
                                <div>
                                  <Text strong style={{ fontSize: 13 }}>Publisher Deals</Text>
                                  <Table
                                    dataSource={dspDealIds.map((deal, idx) => ({
                                      ...deal,
                                      key: deal.dealId || idx,
                                    }))}
                                    rowKey="dealId"
                                    columns={[
                                      {
                                        title: 'Name',
                                        dataIndex: 'name',
                                        key: 'name',
                                        ellipsis: true,
                                      },
                                      {
                                        title: 'Deal ID',
                                        dataIndex: 'dealId',
                                        key: 'dealId',
                                        width: 180,
                                        ellipsis: true,
                                      },
                                      {
                                        title: 'CPM Bid',
                                        dataIndex: 'cpmBid',
                                        key: 'cpmBid',
                                        width: 100,
                                        render: (cpmBid) => (cpmBid ? `$${cpmBid.toFixed(2)}` : '-'),
                                      },
                                      {
                                        title: '',
                                        key: 'action',
                                        width: 60,
                                        render: (_, record) => (
                                          !isFieldDisabled && (
                                            <Button
                                              type="text"
                                              danger
                                              size="small"
                                              icon={<DeleteOutlined />}
                                              onClick={() => {
                                                const campaigns = form.getFieldValue('campaigns') || [];
                                                const updatedCampaigns = [...campaigns];
                                                if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
                                                  updatedCampaigns[campaignIndex].lineItems[lineItemIndex] = {
                                                    ...updatedCampaigns[campaignIndex].lineItems[lineItemIndex],
                                                    dspDealIds: (dspDealIds || []).filter(
                                                      (d) => d.dealId !== record.dealId
                                                    ),
                                                  };
                                                  form.setFieldsValue({ campaigns: updatedCampaigns });
                                                }
                                              }}
                                            />
                                          )
                                        ),
                                      },
                                    ]}
                                    pagination={false}
                                    size="small"
                                    style={{ marginTop: 8 }}
                                  />
                                </div>
                              )}

                              {/* Empty state when no inventory configured */}
                              {(!inventorySources?.length && !inventoryLists?.length && !dspDealIds?.length) && (
                                <Text type="secondary">No inventory configured (will use defaults)</Text>
                              )}

                              {/* Configure Inventory button */}
                              {!isFieldDisabled && (
                                <Button onClick={() => setShowInventoryDialog(true)}>
                                  Configure Inventory
                                </Button>
                              )}
                            </Space>
                          </Card>

                          {/* Nexxen Segments Section */}
                          <Card title="NEXXEN SEGMENTS" size="small">
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                              <div>
                                <Text>Third-Party Targeting Segments</Text>
                                <br />
                                <Text type="secondary">
                                  <small>Select one Nexxen segment set for audience targeting</small>
                                </Text>
                              </div>

                              {segments && segments.length > 0 && (
                                <Table
                                  dataSource={segments.map((segment, idx) => ({
                                    ...segment,
                                    key: segment.segmentId || segment.id || idx,
                                  }))}
                                  rowKey="segmentId"
                                  columns={[
                                    {
                                      title: 'Name',
                                      dataIndex: 'name',
                                      key: 'name',
                                      ellipsis: true,
                                    },
                                    {
                                      title: 'Type',
                                      dataIndex: 'segmentType',
                                      key: 'segmentType',
                                      width: 120,
                                      render: (type) => (
                                        <Tag color={type === 'segmentSet' ? 'blue' : 'green'}>
                                          {type === 'segmentSet' ? 'Segment Set' : 'Segment'}
                                        </Tag>
                                      ),
                                    },
                                    {
                                      title: 'CPM Cost',
                                      dataIndex: 'cost',
                                      key: 'cost',
                                      width: 100,
                                      render: (cost) => (cost > 0 ? `$${cost.toFixed(2)}` : '-'),
                                    },
                                    {
                                      title: '',
                                      key: 'action',
                                      width: 60,
                                      render: (_, record) => (
                                        !isFieldDisabled && (
                                          <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => {
                                              const campaigns = form.getFieldValue('campaigns') || [];
                                              const updatedCampaigns = [...campaigns];
                                              if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
                                                updatedCampaigns[campaignIndex].lineItems[lineItemIndex] = {
                                                  ...updatedCampaigns[campaignIndex].lineItems[lineItemIndex],
                                                  segments: (segments || []).filter(
                                                    (s) => s.segmentId !== record.segmentId
                                                  ),
                                                };
                                                form.setFieldsValue({ campaigns: updatedCampaigns });
                                              }
                                            }}
                                          />
                                        )
                                      ),
                                    },
                                  ]}
                                  pagination={false}
                                  size="small"
                                />
                              )}

                              {/* Empty state when no segments configured */}
                              {(!segments || segments.length === 0) && (
                                <Text type="secondary">No segments configured</Text>
                              )}

                              {/* Segment Lookup button */}
                              {!isFieldDisabled && (
                                <Button
                                  style={{ width: 200 }}
                                  onClick={() => setShowNexxenSegmentsDialog(true)}
                                >
                                  Segment Lookup
                                </Button>
                              )}
                            </Space>
                          </Card>

                          {/* Save Button */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              loading={isSaving || savingDspSettings}
                              disabled={isLaunching || isSaving || savingDspSettings}
                              onClick={() => {
                                if (onSave) {
                                  onSave();
                                } else {
                                  handleSaveDspSettings();
                                }
                              }}
                            >
                              Save DSP Settings
                            </Button>
                          </div>
                        </Space>
                      ),
                    }] : []),
                    // Line Item Details tab
                    {
                      key: 'details',
                      label: 'Line Item Details',
                      children: (
                        <Space direction="vertical" size="middle" style={{ width: '100%', paddingTop: 16, paddingBottom: 24 }}>
                          {/* LINE ITEM DETAILS */}
                          <Row gutter={20}>
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">Line Item Name</Text>
                                <Text>{name}</Text>
                              </Space>
                            </Col>
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">
                                  {isVideoType ? 'Creative Groups' : 'Ad Groups'}
                                </Text>
                                <Text>
                                  {adGroup &&
                                    adGroup.adGroupCreatives &&
                                    adGroup.adGroupCreatives.length}
                                </Text>
                              </Space>
                            </Col>
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">
                                  {isVideoType ? 'Video Assets' : 'Ad Units'}
                                </Text>
                                <Text>
                                  {adGroup &&
                                    adGroup.adGroupCreatives &&
                                    adGroup.adGroupCreatives.reduce((acc, curr) => {
                                      return acc + curr.medias.length;
                                    }, 0)}
                                </Text>
                              </Space>
                            </Col>
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">Start Date</Text>
                                <Text>{moment(startDate).tz(timezone).format('MM/DD/YYYY')}</Text>
                              </Space>
                            </Col>
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">End Date</Text>
                                <Text>{moment(endDate).tz(timezone).format('MM/DD/YYYY')}</Text>
                              </Space>
                            </Col>
                          </Row>
                          <Divider plain></Divider>
                          <Row gutter={20}>
                            {pricingModel !== 'Percentage' && (
                              <Col span={4}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Text type="secondary">Wholesale CPM</Text>
                                  <Text></Text>
                                </Space>
                              </Col>
                            )}
                            {pricingModel !== 'Percentage' && (
                              <Col span={4}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Text type="secondary">CPM</Text>
                                  <Text>{cpm}</Text>
                                </Space>
                              </Col>
                            )}
                            {pricingModel !== 'Percentage' && (
                              <Col span={4}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Text type="secondary">Impressions</Text>
                                  <Text>
                                    {calculateImpressions({
                                      budget: budgetTotal,
                                      cpm,
                                    }).toLocaleString()}
                                  </Text>
                                </Space>
                              </Col>
                            )}
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">Line Item Budget</Text>
                                <Text>{formatCurrency2SigFig(budgetTotal)}</Text>
                              </Space>
                            </Col>
                            <Col span={4}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">End Date</Text>
                                <Text>{moment(endDate).tz(timezone).format('MM/DD/YYYY')}</Text>
                              </Space>
                            </Col>
                          </Row>

                          {/* AUDIENCES */}
                          {/* DISPLAY AUDIENCE DEPENDING AUDIENCE TYPE  */}
                          {audienceType === 'FIRST_PARTY_DATA' && (
                            <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                              <>
                                <div className="requester-table-header">
                                  <Row>
                                    <Col span={12}>
                                      <Title level={5}>FIRST PARTY AUDIENCES</Title>
                                    </Col>
                                  </Row>
                                </div>
                                {!firstPartyDataAudiences.length ? (
                                  <div style={{ padding: 20 }}>
                                    <Space
                                      direction="vertical"
                                      size="small"
                                      align="center"
                                      style={{ width: '100%' }}
                                    ></Space>
                                  </div>
                                ) : (
                                  <Table
                                    rowKey="id"
                                    columns={[
                                      {
                                        title: 'Audience Name',
                                        dataIndex: 'name',
                                        key: 'name',
                                        ellipsis: true,
                                        width: 200,
                                      },
                                      {
                                        title: 'Devices',
                                        dataIndex: 'deviceCount',
                                        key: 'deviceCount',
                                        render: (count) => (count ? count.toLocaleString() : 'N/A'),
                                      },
                                      {
                                        title: 'Match %',
                                        dataIndex: 'matchRate',
                                        key: 'matchRate',
                                        render: (rate) =>
                                          rate ? rate.toFixed(2).toLocaleString() : 'N/A',
                                      },
                                      {
                                        title: 'Status',
                                        dataIndex: 'activated',
                                        key: 'activated',
                                        render: (activated) =>
                                          activated ? 'Activated' : 'Deactivated',
                                      },
                                      {
                                        title: 'Uploaded',
                                        dataIndex: 'createdAt',
                                        key: 'createdAt',
                                        render: (date) => moment.utc(date).format('MM/DD/YYYY hh:mma'),
                                      },
                                    ]}
                                    dataSource={firstPartyDataAudiences}
                                    pagination={false}
                                    className="requester-table"
                                    summary={() => (
                                      <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                          {firstPartyDataAudiences.length} Audiences
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}>
                                          {firstPartyDataAudiences
                                            .reduce((acc, audience) => acc + audience.deviceCount, 0)
                                            .toLocaleString()}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell colSpan={3} index={3}>
                                          {firstPartyDataAudiences
                                            .reduce((acc, audience) => acc + audience.matchRate, 0)
                                            .toFixed(2)
                                            .toLocaleString()}{' '}
                                          %
                                        </Table.Summary.Cell>
                                      </Table.Summary.Row>
                                    )}
                                  />
                                )}
                              </>
                            </div>
                          )}
                          {audienceType === 'ALL_USERS' && (
                            <>
                              <Divider plain></Divider>
                              <Row gutter={20}>
                                <Col span={12}>
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Text type="secondary">Audience Type</Text>
                                    <Text>
                                      {audienceTypeMap[audienceType]} {isRegulated ? 'aged 21+' : ''}
                                    </Text>
                                  </Space>
                                </Col>
                              </Row>
                            </>
                          )}
                          {/* TARGETING */}
                          {/* TARGETING REGION TABLE */}
                          {targetingType === 'REGION' && (
                            <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                              <div className="requester-table-header">
                                <Row>
                                  <Col span={12}>
                                    <Title level={5}>TARGETING: REGION GROUPS</Title>
                                  </Col>
                                </Row>
                              </div>
                              {targetingRegions && targetingRegions.length ? (
                                <Table
                                  rowKey="id"
                                  columns={[
                                    {
                                      title: 'Region Group Name',
                                      dataIndex: 'name',
                                      key: 'name',
                                    },
                                    {
                                      title: 'Regions',
                                      dataIndex: 'regions',
                                      key: 'regions',
                                      render: (regions) =>
                                        regions.map((region) => (
                                          <Tag color="green" key={region}>
                                            {region}
                                          </Tag>
                                        )),
                                    },
                                  ]}
                                  dataSource={targetingRegions}
                                  pagination={false}
                                  className="requester-table"
                                  summary={() =>
                                    targetingRegions && targetingRegions.length ? (
                                      <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                          {targetingRegions.length} Region Groups
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                          {targetingRegions.reduce(
                                            (acc, regionGroup) => acc + regionGroup.regions.length,
                                            0
                                          )}{' '}
                                          Individual Regions
                                        </Table.Summary.Cell>
                                      </Table.Summary.Row>
                                    ) : (
                                      <></>
                                    )
                                  }
                                />
                              ) : (
                                <></>
                              )}
                            </div>
                          )}
                          {/* TARGETING LOCAL TABLE */}
                          {targetingType === 'LOCAL' && (
                            <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                              <div className="requester-table-header">
                                <Row>
                                  <Col span={12}>
                                    <Title level={5}>TARGETING: LOCAL</Title>
                                  </Col>
                                  <Col span={12}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 8,
                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      <Button>
                                        <CSVLink
                                          filename={`${lineItem.name}-Geo.csv`}
                                          style={{ textDecoration: 'none' }}
                                          data={csvData}
                                        >
                                          <DownloadOutlined />
                                          Download CSV
                                        </CSVLink>
                                      </Button>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                              {targetingLocations && targetingLocations.length ? (
                                <Table
                                  rowKey="id"
                                  columns={[
                                    {
                                      title: 'Targeting Group Name',
                                      dataIndex: 'name',
                                      key: 'name',
                                    },
                                    {
                                      title: 'Locations',
                                      dataIndex: 'locations',
                                      key: 'locations',
                                      width: '40%',
                                      render: (_, { geoTargets }) => (
                                        <div>
                                          {geoTargets &&
                                            geoTargets.map((target) => {
                                              return target.location ? (
                                                <Tag color="blue" key={target.id}>
                                                  {target.location.name}
                                                </Tag>
                                              ) : (
                                                <></>
                                              );
                                            })}
                                        </div>
                                      ),
                                    },
                                    {
                                      title: 'Created On',
                                      dataIndex: 'createdAt',
                                      key: 'createdAt',
                                      render: (text) => moment.utc(text).format('MM/DD/YYYY hh:mma'),
                                    },
                                  ]}
                                  dataSource={targetingLocations}
                                  pagination={false}
                                  className="requester-table"
                                  summary={() =>
                                    targetingLocations && targetingLocations.length ? (
                                      <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                          {targetingLocations.length} Targeting Groups
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                          {targetingLocations.reduce(
                                            (acc, targetingGroup) =>
                                              acc + targetingGroup.geoTargets.length,
                                            0
                                          )}{' '}
                                          Targeting Locations
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                      </Table.Summary.Row>
                                    ) : (
                                      <></>
                                    )
                                  }
                                />
                              ) : (
                                <></>
                              )}
                            </div>
                          )}
                          {targetingType === 'COUNTRY' && (
                            <>
                              <Divider plain></Divider>
                              <Row gutter={20}>
                                <Col span={12}>
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Text type="secondary">Targeting Type</Text>
                                    <Text>
                                      {targetingType}:{' '}
                                      {targetingCountries.map((country) => country.name).join(', ')}
                                    </Text>
                                  </Space>
                                </Col>
                              </Row>
                            </>
                          )}
                          {/* CREATIVE ASSETS */}
                          {adGroupForUI && adGroupForUI.adGroupCreatives.length ? (
                            isVideoType ? (
                              <CampaignOrderQueueReviewVideoAssets
                                adGroup={adGroupForUI}
                                campaignIndex={campaignIndex}
                                lineItemIndex={lineItemIndex}
                                onMediaStatusUpdate={onMediaStatusUpdate}
                              />
                            ) : (
                              adGroupForUI.adGroupCreatives.map((adGroupCreative, adGroupCreativeIndex) => (
                                <CampaignOrderQueueReviewAdGroup
                                  key={adGroupCreativeIndex}
                                  adGroup={adGroupForUI}
                                  adGroupCreative={adGroupCreative}
                                  adGroupCreativeIndex={adGroupCreativeIndex}
                                  campaignIndex={campaignIndex}
                                  lineItemIndex={lineItemIndex}
                                  onMediaStatusUpdate={onMediaStatusUpdate}
                                />
                              ))
                            )
                          ) : (
                            <></>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Space>
          </Card>
        </Panel>
      </Collapse>

      {/* Inventory Configuration Dialog */}
      {showInventoryDialog && (
        <CampaignRequestorInventoryDialog
          visible={showInventoryDialog}
          setVisible={setShowInventoryDialog}
          selectedInventorySources={inventorySources || []}
          selectedInventoryLists={inventoryLists || []}
          selectedPublisherDeals={dspDealIds || []}
          campaignType={campaignType}
          onSaveInventoryConfig={handleSaveInventoryConfig}
        />
      )}

      {/* Nexxen Segments Dialog */}
      {showNexxenSegmentsDialog && (
        <CampaignRequestorNexxenSegmentsDialog
          visible={showNexxenSegmentsDialog}
          setVisible={setShowNexxenSegmentsDialog}
          selectedSegments={segments || []}
          advertiserDspId={currentCampaignOrder?.advertiser?.dataConfig?.dspAdvertiserId}
          onAddSelections={handleSaveSegments}
        />
      )}
    </div>
  );
};
